import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { KENO_API, gameHeaders } from '../keno/fairnessApi';
import { currentChatRestriction, isChatRestriction } from './chatRestriction';

const snapshot = (owner, data) => ({ owner, data, serverAt: Date.parse(data.server_time) || Date.now(), receivedAt: performance.now() });

export const useChatRestriction = (open, user) => {
  const owner = user?.id;
  const latestOwner = useRef(owner); latestOwner.current = owner;
  const version = useRef(0);
  const [state, setState] = useState(null);
  const [, tick] = useState(0);
  useEffect(() => {
    version.current += 1;
    setState(owner ? snapshot(owner, { chat_timeout: user?.chat_timeout, chat_ban: user?.chat_ban }) : null);
    // Profile is only an initial seed; authoritative polling must be able to lift it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [owner]);

  useEffect(() => {
    if (!open || !owner) return;
    const abort = new AbortController();
    let loading = false;
    const load = async () => {
      if (loading) return;
      loading = true;
      const current = version.current;
      try {
        const { data } = await axios.get(`${KENO_API}/chat/status`, { headers: gameHeaders(false), signal: abort.signal, timeout: 10000 });
        if (!abort.signal.aborted && current === version.current && latestOwner.current === owner) setState(snapshot(owner, data));
      } catch (_) {
        // Keep the last known deadline, and retry. Sending is still guarded server-side.
      } finally { loading = false; }
    };
    load();
    const poll = setInterval(load, 2500), clock = setInterval(() => tick((value) => value + 1), 1000);
    window.addEventListener('focus', load);
    return () => { abort.abort(); clearInterval(poll); clearInterval(clock); window.removeEventListener('focus', load); };
  }, [open, owner]);

  const acceptRestrictionError = useCallback((detail) => {
    if (!isChatRestriction(detail)) return false;
    if (latestOwner.current === owner) {
      version.current += 1; // A pre-403 status response cannot overwrite this newer restriction.
      setState((previous) => snapshot(owner, { ...(previous?.owner === owner ? previous.data : {}), [detail.code]: { ...detail, active: true }, server_time: detail.server_time }));
    }
    return true;
  }, [owner]);

  const now = state ? state.serverAt + performance.now() - state.receivedAt : Date.now();
  return { restriction: owner && state?.owner === owner ? currentChatRestriction(state.data, now) : null, acceptRestrictionError };
};